import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { Video, Play, Clock, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import BottomNavigation from '../components/BottomNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentLive() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLiveClasses();
  }, []);

  const fetchLiveClasses = async () => {
    try {
      const response = await axios.get(`${API}/live-classes`, getAuthHeader());
      setLiveClasses(response.data);
    } catch (error) {
      toast.error('Failed to load live classes');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClass = (classId) => {
    navigate(`/live-class/${classId}`);
  };

  // Separate live, upcoming, and past classes
  const now = new Date();
  const liveNow = liveClasses.filter(c => c.status === 'live');
  const upcoming = liveClasses.filter(c => c.status === 'scheduled' && new Date(c.scheduled_at) > now);
  const past = liveClasses.filter(c => c.status === 'ended' || (c.status === 'scheduled' && new Date(c.scheduled_at) < now));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-500 to-pink-500 text-white sticky top-0 z-40">
        <div className="px-4 py-6">
          <div className="flex items-center space-x-3 mb-2">
            <Video className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Live Classes</h1>
          </div>
          <p className="text-white/80 text-sm">Join live sessions with your teachers</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6">
        {/* Live Now Section */}
        {liveNow.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center">
              <span className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
              Live Now
            </h2>
            <div className="space-y-3">
              {liveNow.map((liveClass) => (
                <Card
                  key={liveClass.id}
                  data-testid={`live-class-${liveClass.id}`}
                  className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl shadow-lg overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-semibold flex items-center">
                        <span className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></span>
                        LIVE
                      </span>
                      <span className="text-sm text-white/80">{liveClass.duration_minutes} min</span>
                    </div>
                    <h3 className="font-bold text-lg mb-1">{liveClass.title}</h3>
                    <p className="text-sm text-white/80 mb-3">{liveClass.description}</p>
                    <Button
                      data-testid={`join-live-${liveClass.id}`}
                      onClick={() => handleJoinClass(liveClass.id)}
                      className="w-full bg-white text-red-600 hover:bg-white/90 rounded-full font-semibold"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Join Now
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Classes */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-3">Upcoming Classes</h2>
          {upcoming.length === 0 ? (
            <Card className="bg-white rounded-xl p-6 text-center shadow-sm">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 text-sm">No upcoming classes scheduled</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcoming.map((liveClass) => (
                <Card
                  key={liveClass.id}
                  data-testid={`upcoming-class-${liveClass.id}`}
                  className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900">{liveClass.title}</h3>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{liveClass.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-slate-500 mt-3">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(liveClass.scheduled_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {new Date(liveClass.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="flex items-center">
                        <Video className="w-4 h-4 mr-1" />
                        {liveClass.duration_minutes} min
                      </span>
                    </div>
                    <Button
                      onClick={() => handleJoinClass(liveClass.id)}
                      variant="outline"
                      className="w-full mt-3 rounded-full"
                    >
                      Set Reminder
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Past Classes / Recordings */}
        {past.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Past Recordings</h2>
            <div className="space-y-3">
              {past.slice(0, 5).map((liveClass) => (
                <Card
                  key={liveClass.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Video className="w-6 h-6 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{liveClass.title}</h3>
                        <p className="text-xs text-slate-500">
                          {new Date(liveClass.scheduled_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="rounded-full">
                        <Play className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {liveClasses.length === 0 && (
          <Card className="bg-white rounded-xl p-8 text-center shadow-sm">
            <Video className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No live classes available</p>
            <p className="text-sm text-slate-400 mt-1">Check back later for scheduled sessions</p>
          </Card>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
}
